/**
 * English-only validation utilities
 * For the messaging feature that requires users to practice English.
 *
 * Strategy: only block non-Latin SCRIPT characters (CJK, Cyrillic, Arabic,
 * Devanagari, Thai, Korean, etc.). All punctuation, symbols, numbers,
 * spaces, and emojis are always allowed regardless of Unicode range.
 */

/**
 * Returns true if a codepoint belongs to a non-Latin script (should be blocked).
 */
function isNonLatinScript(code: number): boolean {
    // CJK Unified Ideographs (Chinese/Japanese Kanji)
    if (code >= 0x4E00 && code <= 0x9FFF) return true;
    // CJK Extension A
    if (code >= 0x3400 && code <= 0x4DBF) return true;
    // CJK Extension B
    if (code >= 0x20000 && code <= 0x2A6DF) return true;
    // CJK Compatibility Ideographs
    if (code >= 0xF900 && code <= 0xFAFF) return true;
    // CJK Radicals / Strokes / Symbols
    if (code >= 0x2E80 && code <= 0x2FDF) return true;
    // CJK Symbols and Punctuation (fullwidth。、「」etc.)
    if (code >= 0x3000 && code <= 0x303F) return true;

    // Hiragana
    if (code >= 0x3040 && code <= 0x309F) return true;
    // Katakana
    if (code >= 0x30A0 && code <= 0x30FF) return true;
    // Katakana Phonetic Extensions
    if (code >= 0x31F0 && code <= 0x31FF) return true;

    // Hangul (Korean)
    if (code >= 0xAC00 && code <= 0xD7AF) return true;
    // Hangul Jamo
    if (code >= 0x1100 && code <= 0x11FF) return true;
    // Hangul Compatibility Jamo
    if (code >= 0x3130 && code <= 0x318F) return true;

    // Cyrillic
    if (code >= 0x0400 && code <= 0x04FF) return true;
    // Cyrillic Supplement
    if (code >= 0x0500 && code <= 0x052F) return true;

    // Arabic
    if (code >= 0x0600 && code <= 0x06FF) return true;
    // Arabic Supplement
    if (code >= 0x0750 && code <= 0x077F) return true;
    // Arabic Extended
    if (code >= 0x08A0 && code <= 0x08FF) return true;

    // Devanagari (Hindi)
    if (code >= 0x0900 && code <= 0x097F) return true;
    // Bengali
    if (code >= 0x0980 && code <= 0x09FF) return true;
    // Gurmukhi (Punjabi)
    if (code >= 0x0A00 && code <= 0x0A7F) return true;
    // Gujarati
    if (code >= 0x0A80 && code <= 0x0AFF) return true;
    // Tamil
    if (code >= 0x0B80 && code <= 0x0BFF) return true;
    // Telugu
    if (code >= 0x0C00 && code <= 0x0C7F) return true;
    // Kannada
    if (code >= 0x0C80 && code <= 0x0CFF) return true;
    // Malayalam
    if (code >= 0x0D00 && code <= 0x0D7F) return true;

    // Thai
    if (code >= 0x0E00 && code <= 0x0E7F) return true;
    // Lao
    if (code >= 0x0E80 && code <= 0x0EFF) return true;

    // Myanmar
    if (code >= 0x1000 && code <= 0x109F) return true;
    // Khmer
    if (code >= 0x1780 && code <= 0x17FF) return true;

    // Georgian
    if (code >= 0x10A0 && code <= 0x10FF) return true;
    // Armenian
    if (code >= 0x0530 && code <= 0x058F) return true;
    // Hebrew
    if (code >= 0x0590 && code <= 0x05FF) return true;
    // Ethiopic
    if (code >= 0x1200 && code <= 0x137F) return true;
    // Tibetan
    if (code >= 0x0F00 && code <= 0x0FFF) return true;
    // Sinhala
    if (code >= 0x0D80 && code <= 0x0DFF) return true;

    // Fullwidth Latin letters and halfwidth Katakana
    if (code >= 0xFF00 && code <= 0xFFEF) return true;

    // Bopomofo (Chinese phonetic)
    if (code >= 0x3100 && code <= 0x312F) return true;
    if (code >= 0x31A0 && code <= 0x31BF) return true;

    return false;
}

/**
 * Check if text contains only English-compatible characters.
 * Blocks non-Latin scripts; allows ALL punctuation, symbols, and emojis.
 */
export function isEnglishOnly(text: string): boolean {
    if (!text) return true;

    for (const char of text) {
        const code = char.codePointAt(0);
        if (code === undefined) continue;
        if (isNonLatinScript(code)) return false;
    }

    return true;
}

/**
 * Check if a single character is non-English (non-Latin script).
 */
export function isNonEnglishCharacter(char: string): boolean {
    const code = char.codePointAt(0);
    if (code === undefined) return false;
    return isNonLatinScript(code);
}

/**
 * Get non-English characters from a string.
 */
export function getNonEnglishCharacters(text: string): string[] {
    if (!text) return [];

    const nonEnglish: string[] = [];
    for (const char of text) {
        if (isNonEnglishCharacter(char) && !nonEnglish.includes(char)) {
            nonEnglish.push(char);
        }
    }
    return nonEnglish;
}

/**
 * Filter out non-English characters from text.
 */
export function filterToEnglishOnly(text: string): string {
    if (!text) return '';
    return Array.from(text).filter(char => !isNonEnglishCharacter(char)).join('');
}
